/**
 * AccountPage — tabbed shell for user account: profile, security,
 * preferences. Active tab is driven by the ?tab=... query param.
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router";
import { Footer } from "@/components/Footer";
import { AdminPageHeader } from "@/routes/admin/AdminPageHeader";
import { useAuth } from "@/lib/hooks/useAuth";
import { ProfileTab } from "@/routes/account/ProfileTab";
import { SecurityTab } from "@/routes/account/SecurityTab";
import { PreferencesTab } from "@/routes/account/PreferencesTab";

type TabKey = "profiel" | "beveiliging" | "voorkeuren";

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: "profiel", label: "Profiel" },
  { key: "beveiliging", label: "Beveiliging" },
  { key: "voorkeuren", label: "Voorkeuren" },
];

function isTabKey(value: string | null): value is TabKey {
  return value === "profiel" || value === "beveiliging" || value === "voorkeuren";
}

export function AccountPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab: TabKey = isTabKey(rawTab) ? rawTab : "profiel";

  const selectTab = useCallback(
    (key: TabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", key);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="flex min-h-screen flex-col bg-[var(--cs-ivory)] text-[var(--cs-navy)]">
      <AdminPageHeader
        title="Mijn account"
        user={auth.user}
        accessRole={auth.accessRole}
        showLogo
      />

      <nav
        aria-label="Account secties"
        className="border-b border-[rgba(10,52,96,0.10)] bg-white"
      >
        <div className="flex gap-1 overflow-x-auto px-4 sm:px-6">
          {TABS.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                aria-current={isActive ? "page" : undefined}
                className={
                  "relative shrink-0 px-3 py-3 text-sm font-medium transition-colors " +
                  (isActive
                    ? "text-[var(--cs-navy)]"
                    : "text-[rgba(10,52,96,0.62)] hover:text-[var(--cs-navy)]")
                }
              >
                {tab.label}
                {isActive ? (
                  <span
                    aria-hidden
                    className="absolute inset-x-2 -bottom-px h-[2px] rounded-t bg-[var(--cs-rose)]"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="w-full flex-1 space-y-4 px-4 py-6 sm:px-6">
        {activeTab === "profiel" ? <ProfileTab /> : null}
        {activeTab === "beveiliging" ? <SecurityTab /> : null}
        {activeTab === "voorkeuren" ? <PreferencesTab /> : null}
      </main>

      <Footer />
    </div>
  );
}
